/**
 * COMMERCE-176D-R1 recovery — persist collection sort/limit (and in-collection query)
 * across CatalogListV2 / PromotionsV2 remounts when navigating Back from PDP.
 * Scroll restore stays in StoreV2App scrollMemory — this module does not touch scroll.
 */

import type { CatalogSortKey } from "./catalog-sort-v2.js";
import { normalizeCatalogSortKey } from "./catalog-sort-v2.js";

const STORAGE_PREFIX = "asiastyle.store-v2.collection-nav.v1:";

export type CollectionNavStateV2 = {
  collectionKey: string;
  sort: CatalogSortKey;
  limit: number;
  inCollectionQuery?: string;
};

/** In-memory fallback when sessionStorage is unavailable. */
const memoryStore = new Map<string, CollectionNavStateV2>();

export function collectionKeyFrom(type: string, id?: string | null): string {
  const t = String(type ?? "")
    .trim()
    .toLowerCase();
  const raw = id == null ? "" : String(id).trim();
  if (t === "new" || t === "promotions") return t;
  if (!raw) return t || "unknown";
  if (t === "search") return `search:${raw}`;
  if (t === "category") return `category:${raw}`;
  if (t === "brand") return `brand:${raw}`;
  if (t === "country") return `country:${raw}`;
  return `${t}:${raw}`;
}

function storageKey(collectionKey: string): string {
  return `${STORAGE_PREFIX}${collectionKey}`;
}

function sanitizeLimit(raw: unknown, pageSize: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < pageSize) return pageSize;
  // Keep multiples of pageSize when possible; never below one page.
  return Math.max(pageSize, Math.floor(n));
}

export function readCollectionNavState(
  key: string,
  pageSize = 24,
): CollectionNavStateV2 | null {
  const collectionKey = String(key ?? "").trim();
  if (!collectionKey) return null;

  const fromMemory = memoryStore.get(collectionKey);
  let parsed: Partial<CollectionNavStateV2> | null = fromMemory ?? null;

  try {
    const raw = window.sessionStorage.getItem(storageKey(collectionKey));
    if (raw) {
      parsed = JSON.parse(raw) as Partial<CollectionNavStateV2>;
    }
  } catch {
    /* use memory / null */
  }

  if (!parsed) return null;
  const sort = normalizeCatalogSortKey(parsed.sort);
  const limit = sanitizeLimit(parsed.limit, pageSize);
  const out: CollectionNavStateV2 = {
    collectionKey,
    sort,
    limit,
  };
  const q = String(parsed.inCollectionQuery ?? "").trim();
  if (q) out.inCollectionQuery = q.slice(0, 200);
  return out;
}

export function writeCollectionNavState(key: string, state: CollectionNavStateV2): void {
  const collectionKey = String(key ?? "").trim();
  if (!collectionKey) return;
  const payload: CollectionNavStateV2 = {
    collectionKey,
    sort: normalizeCatalogSortKey(state.sort),
    limit: Math.max(1, Math.floor(Number(state.limit) || 24)),
  };
  const q = String(state.inCollectionQuery ?? "").trim();
  if (q) payload.inCollectionQuery = q.slice(0, 200);

  memoryStore.set(collectionKey, payload);
  try {
    window.sessionStorage.setItem(storageKey(collectionKey), JSON.stringify(payload));
  } catch {
    /* memory fallback only */
  }
}
